import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  EmployeeInMarketResearchKeys,
  useEmployeeInMarketResearchQuery,
} from '@/queries/useEmployeeInMarketResearchQuery';
import { PDVInMarketResearchKeys } from '@/queries/usepdvInMarketResearchQuery';
import { EmployeeKeys } from '@/queries/useEmployeeQuery';
import { saveEmployeesInMarketResearch } from '@/api/business/marketResearch';
import { isAxiosError } from '@/api/business';
import { useToast } from '@/components/ui/use-toast';
import { Switch } from '@/components/ui/switch';
import Button from '@/components/Button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { TypographyMuted } from '@/components/ui/typography';
import LoadingScreen from '@/pages/LoadingScreen';
import { EmployeeMarketResearch } from '@/api/business/marketResearch/type';
import { CheckSquare, Filter, Save, Square, Users } from 'lucide-react';
import { useBaseStore } from '@/store';
import { authStateSelector } from '@/store/authSlice';
import { decodeJwt } from 'jose';

interface TableEmployeesProps {
  id: number;
  status?: string;
}

const TableEmployeesMarketResearch = ({ id, status }: TableEmployeesProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading } = useEmployeeInMarketResearchQuery(Number(id));

  const { token } = useBaseStore(authStateSelector);
  const decodedToken = useMemo(() => {
    if (!token) return null;
    try {
      return decodeJwt(token) as any;
    } catch {
      return null;
    }
  }, [token]);

  const perfil = decodedToken?.usuario?.perfil || decodedToken?.perfil;

  // Estado local para controle das seleções dos funcionários (SK_FUNCIONARIO -> 1 ou 0)
  const [employeeStates, setEmployeeStates] = useState<Record<string, number>>(
    {},
  );
  const [searchFilter, setSearchFilter] = useState('');
  const isDraft = status === 'Rascunho';

  useEffect(() => {
    if (data) {
      const initialMap: Record<string, number> = {};
      data.forEach((emp) => {
        initialMap[emp.SK_FUNCIONARIO] =
          emp.DD_PARTICIPA_PESQUISA !== 0 ? 1 : 0;
      });
      setEmployeeStates(initialMap);
    }
  }, [data]);

  const { mutate, isPending } = useMutation({
    mutationFn: saveEmployeesInMarketResearch,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: EmployeeInMarketResearchKeys.detail(id),
      });
      queryClient.invalidateQueries({
        queryKey: PDVInMarketResearchKeys.detail(id),
      });
      queryClient.invalidateQueries({ queryKey: EmployeeKeys.lists() });
      toast({
        title: 'Funcionários salvos com sucesso',
        description:
          'A parametrização de participação de vendedores foi atualizada.',
      });
    },
    onError: (err) => {
      let errorMessage =
        'Não foi possível salvar os funcionários. Tente novamente mais tarde.';
      if (isAxiosError(err)) {
        errorMessage = err.response?.data.message || errorMessage;
      }
      toast({
        title: 'Erro ao salvar funcionários',
        description: errorMessage,
        variant: 'destructive',
      });
    },
  });

  const employeesByResale = useMemo(() => {
    if (!data) return {};
    const filtered = data.filter((emp) => {
      if (!searchFilter.trim()) return true;
      const term = searchFilter.toLowerCase();
      return (
        (emp.NM_FUNCIONARIO &&
          emp.NM_FUNCIONARIO.toLowerCase().includes(term)) ||
        (emp.NK_FUNCIONARIO &&
          emp.NK_FUNCIONARIO.toLowerCase().includes(term)) ||
        (emp.DD_CARGO && emp.DD_CARGO.toLowerCase().includes(term)) ||
        (emp.DS_REVENDA && emp.DS_REVENDA.toLowerCase().includes(term))
      );
    });

    const grouped: Record<string, EmployeeMarketResearch[]> = {};
    filtered.forEach((emp) => {
      const resaleName = emp.DS_REVENDA || 'Revenda Desconhecida';
      if (!grouped[resaleName]) {
        grouped[resaleName] = [];
      }
      grouped[resaleName].push(emp);
    });

    return grouped;
  }, [data, searchFilter]);

  const totalEmployees = data?.length || 0;
  const activeCount = Object.values(employeeStates).filter(
    (v) => v === 1,
  ).length;

  const handleToggleEmployee = (skFuncionario: string) => {
    if (!isDraft) return;
    setEmployeeStates((prev) => ({
      ...prev,
      [skFuncionario]: prev[skFuncionario] === 1 ? 0 : 1,
    }));
  };

  const handleSelectResaleAll = (
    employeesInResale: EmployeeMarketResearch[],
  ) => {
    if (!isDraft) return;
    setEmployeeStates((prev) => {
      const next = { ...prev };
      employeesInResale.forEach((emp) => {
        next[emp.SK_FUNCIONARIO] = 1;
      });
      return next;
    });
  };

  const handleDeselectResaleAll = (
    employeesInResale: EmployeeMarketResearch[],
  ) => {
    if (!isDraft) return;
    setEmployeeStates((prev) => {
      const next = { ...prev };
      employeesInResale.forEach((emp) => {
        next[emp.SK_FUNCIONARIO] = 0;
      });
      return next;
    });
  };

  const handleSelectAllGlobal = () => {
    if (!isDraft || !data) return;
    setEmployeeStates((prev) => {
      const next = { ...prev };
      data.forEach((emp) => {
        next[emp.SK_FUNCIONARIO] = 1;
      });
      return next;
    });
  };

  const handleDeselectAllGlobal = () => {
    if (!isDraft || !data) return;
    setEmployeeStates((prev) => {
      const next = { ...prev };
      data.forEach((emp) => {
        next[emp.SK_FUNCIONARIO] = 0;
      });
      return next;
    });
  };

  const handleSave = () => {
    if (!data) return;
    const payload = Object.entries(employeeStates).map(
      ([skFuncionario, statusVal]) => ({
        SK_FUNCIONARIO: skFuncionario,
        DD_PARTICIPA_PESQUISA: statusVal,
      }),
    );

    mutate({
      SK_PESQUISA: id,
      EMPLOYEES: payload,
      perfil: perfil,
    } as any);
  };

  if (isLoading) return <LoadingScreen />;

  return (
    <div className="flex flex-col gap-4 p-2 bg-white border border-gray-100 rounded-lg shadow-sm">
      <div className="flex flex-col gap-3 pb-3 border-b border-gray-100 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" />
          <div>
            <h4 className="text-sm font-semibold text-gray-900">
              Funcionários (Vendedores) da Pesquisa
            </h4>
            <TypographyMuted className="text-xs">
              Selecione os vendedores participantes ({activeCount} de{' '}
              {totalEmployees} ativos)
            </TypographyMuted>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isDraft && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={handleSelectAllGlobal}
              >
                <CheckSquare className="w-3.5 h-3.5 mr-1" />
                Marcar Todos
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-8 text-xs"
                onClick={handleDeselectAllGlobal}
              >
                <Square className="w-3.5 h-3.5 mr-1" />
                Desmarcar Todos
              </Button>
              <Button
                size="sm"
                className="text-xs h-8 gap-1.5"
                onClick={handleSave}
                disabled={isPending}
              >
                <Save className="w-3.5 h-3.5" />
                {isPending ? 'Salvando...' : 'Salvar Funcionários'}
              </Button>
            </>
          )}
          {!isDraft && (
            <Badge variant="secondary" className="text-xs">
              Somente leitura (Pesquisa {status})
            </Badge>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Filter className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
          <Input
            placeholder="Filtrar por vendedor, código, cargo ou revenda..."
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            className="text-xs pl-9 h-9"
          />
        </div>
      </div>

      {Object.keys(employeesByResale).length === 0 ? (
        <div className="py-8 text-sm text-center text-gray-500">
          Nenhum funcionário encontrado para as revendas selecionadas nesta
          pesquisa.
        </div>
      ) : (
        <div className="flex flex-col gap-4 max-h-[380px] overflow-y-auto pr-1">
          {Object.entries(employeesByResale).map(([resaleName, employees]) => {
            const resaleActiveCount = employees
              .filter((emp) => employeeStates[emp.SK_FUNCIONARIO] === 1)
              .filter((i) => i.DD_TIPO_VENDEDOR !== 'INATIVO').length;

            return (
              <div
                key={resaleName}
                className="flex flex-col gap-2 p-3 border rounded-md bg-gray-50/70 border-gray-200/60"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-gray-800">
                      {resaleName}
                    </span>
                    <Badge variant="outline" className="text-[10px] bg-white">
                      {resaleActiveCount} /{' '}
                      {
                        employees?.filter(
                          (i) => i.DD_TIPO_VENDEDOR !== 'INATIVO',
                        ).length
                      }{' '}
                      ativos
                    </Badge>
                  </div>
                  {isDraft && (
                    <div className="flex items-center gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => handleSelectResaleAll(employees)}
                        className="text-primary hover:underline font-medium text-[11px]"
                      >
                        Marcar revenda
                      </button>
                      <span className="text-gray-300">|</span>
                      <button
                        type="button"
                        onClick={() => handleDeselectResaleAll(employees)}
                        className="text-gray-500 hover:underline font-medium text-[11px]"
                      >
                        Desmarcar revenda
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-2 mt-1 sm:grid-cols-2 md:grid-cols-3">
                  {employees
                    ?.filter((i) => i.DD_TIPO_VENDEDOR !== 'INATIVO')
                    .map((emp) => {
                      const isChecked =
                        employeeStates[emp.SK_FUNCIONARIO] === 1;
                      return (
                        <div
                          key={emp.SK_FUNCIONARIO}
                          onClick={() =>
                            isDraft && handleToggleEmployee(emp.SK_FUNCIONARIO)
                          }
                          className={`flex items-start justify-between p-2.5 rounded border text-xs cursor-pointer transition-colors ${
                            isChecked
                              ? 'bg-primary/5 border-primary/30 text-gray-900 font-medium'
                              : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                          } ${!isDraft ? 'cursor-default opacity-80' : ''}`}
                        >
                          <div className="flex flex-col min-w-0 pr-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="font-medium text-gray-900 truncate">
                                {emp.NM_FUNCIONARIO}
                              </span>
                              {Boolean(emp.TEM_RELACIONAMENTO_USUARIO) ? (
                                <Badge
                                  variant="outline"
                                  className="text-[9px] px-1 py-0 h-4 border-emerald-200 bg-emerald-50 text-emerald-700 font-normal shrink-0"
                                  title={`Usuário vinculado: ${emp.NM_USUARIO_RELACIONADO || 'Sim'}`}
                                >
                                  Com Usuário
                                </Badge>
                              ) : (
                                <Badge
                                  variant="outline"
                                  className="text-[9px] px-1 py-0 h-4 border-gray-200 bg-gray-50 text-gray-400 font-normal shrink-0"
                                  title="Sem usuário do sistema vinculado"
                                >
                                  Sem Usuário
                                </Badge>
                              )}
                            </div>
                            <span className="text-[10px] text-gray-400 truncate">
                              Cód: {emp.NK_FUNCIONARIO || emp.SK_FUNCIONARIO}
                              {emp.DD_CARGO && ` • ${emp.DD_CARGO}`}
                              {emp.NM_USUARIO_RELACIONADO &&
                                ` • Usuário: ${emp.NM_USUARIO_RELACIONADO}`}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Switch
                              checked={isChecked}
                              disabled={!isDraft}
                              onCheckedChange={() =>
                                handleToggleEmployee(emp.SK_FUNCIONARIO)
                              }
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default TableEmployeesMarketResearch;
